import { pageWrap, tableHeaderCell, tableRow, btnPrimary, minutesFmt } from "../utils/pageStyles";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { fetchEligibleLabs } from "../api/eligibleLabsApi";
import { useRef } from "react";
import ContextualOnboarding from "../components/ContextualOnboarding";

export default function MyEligibleLabsPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [packId, setPackId] = useState("");
  const [wallets, setWallets] = useState([]);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  const filterSelectRef = useRef(null);
  const dataGridBoxRef = useRef(null);

  const load = async (selectedPackId) => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchEligibleLabs(selectedPackId || undefined);
      setWallets(data.wallets || []);
      setItems(data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.message || "Erreur de chargement");
      setWallets([]);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(undefined);
  }, []);

  const packsOptions = useMemo(() => {
    const uniq = new Map();
    for (const w of wallets) {
      if (w.pack_id) uniq.set(w.pack_id, w.pack_code || `Pack#${w.pack_id}`);
    }
    return Array.from(uniq.entries()).map(([id, label]) => ({ id, label }));
  }, [wallets]);

  const columns = useMemo(
    () => [
      { field: "id", headerName: t('eligible_labs.col_id'), width: 90 },
      { field: "name", headerName: t('eligible_labs.col_lab'), flex: 1, minWidth: 200 },
      {
        field: "tags",
        headerName: t('eligible_labs.col_tags'),
        flex: 1,
        minWidth: 220,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
            {(params.value || []).map((t) => (
              <Chip key={t} size="small" label={t} />
            ))}
          </Stack>
        ),
      },
      {
        field: "allowed_by",
        headerName: t('eligible_labs.allowed_by'),
        flex: 1,
        minWidth: 240,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
            {(params.value || []).map((p) => (
              <Chip
                key={`${p.pack_id}-${p.pack_code}`}
                size="small"
                variant="outlined"
                label={p.pack_code || `Pack#${p.pack_id}`}
              />
            ))}
          </Stack>
        ),
      },
    ],
    []
  );

  const walletBlock = (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('eligible_labs.my_packs')}
        </Typography>

        {wallets.length === 0 ? (
          <Alert severity="warning">{t('eligible_labs.no_wallet')}</Alert>
        ) : (
          <Stack spacing={1}>
            {wallets.map((w) => (
              <Box key={w.wallet_id} sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">
                  {w.pack_code || `Pack#${w.pack_id}`} (wallet #{w.wallet_id})
                </Typography>
                <Typography variant="body2">
                  {w.remaining_minutes} / {w.total_minutes} min
                </Typography>
              </Box>
            ))}
          </Stack>
        )}

        <Divider sx={{ my: 2 }} />

        <FormControl fullWidth size="small" ref={filterSelectRef}>
          <InputLabel>{t('eligible_labs.filter_pack')}</InputLabel>
          <Select
            label={t('eligible_labs.filter_pack')}
            value={packId}
            onChange={async (e) => {
              const v = e.target.value;
              setPackId(v);
              await load(v ? Number(v) : undefined);
            }}
          >
            <MenuItem value="">{t('eligible_labs.all_packs')}</MenuItem>
            {packsOptions.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={pageWrap}>
      <Typography variant="h5" gutterBottom>
        {t('eligible_labs.title')}
      </Typography>

      {walletBlock}

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">{t('eligible_labs.loading')}</Typography>
            </Box>
          ) : items.length === 0 ? (
            <Alert severity="info">{t('eligible_labs.no_labs')}</Alert>
          ) : (
            <Box sx={{ height: 520, width: "100%" }} ref={dataGridBoxRef}>
              <DataGrid
                rows={items}
                columns={columns}
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                disableRowSelectionOnClick
                getRowId={(r) => r.id}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <ContextualOnboarding
        pageKey="labs"
        steps={[
          {
            title: t("onboarding.labs.step1_title"),
            text: t("onboarding.labs.step1_text"),
            targetRef: filterSelectRef,
            placement: "bottom",
          },
          {
            title: t("onboarding.labs.step2_title"),
            text: t("onboarding.labs.step2_text"),
            targetRef: dataGridBoxRef,
            placement: "top",
          },
        ]}
      />
    </Box>
  );
}
